import type { User } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, Timestamp } from 'firebase/firestore';

import { fireStore } from 'utils/firebase';
import { fetchFailedError, notFoundError, unauthorizedError } from 'api/errors';
import { PROJECT_ORDER_REGISTRATION, PROJECT_STATUS_CONTRACTED, PROJECT_STATUS_DEPOSIT_COMPLETED } from 'consts';
import { Client, PageStyle, ProfileDetail, ProfilePhoto, Project, Sale } from 'models';

/**
 * API・SQL停止後のデータ置き場（Firestore）
 *
 * managementUsers/{uid}                      : { profile?: ProfileDetail & { name }, pageStyle?: PageStyle }
 * managementUsers/{uid}/projects/{id}        : Project（client は埋め込み）+ createdAt
 * managementUsers/{uid}/clients/{id}         : Client
 * managementUsers/{uid}/achievementPhotos/{id}: ProfilePhoto
 *
 * 読み取りはセキュリティルールで本人（request.auth.uid == uid）に限定し、書き込みは管理者SDKのみとする。
 */
export const MANAGEMENT_USERS_COLLECTION = 'managementUsers';

interface ManagementUserDoc {
  profile?: ProfileDetail & { name?: string };
  pageStyle?: PageStyle;
}

type ProjectDoc = Project & { createdAt?: Timestamp };

const requireUid = (user: User | null | undefined): string => {
  if (!user) throw unauthorizedError();
  return user.uid;
};

// Firestoreの例外は詳細を隠して共通エラーへ変換する（既存の404などはそのまま通す）
const wrapRead = async <T>(read: () => Promise<T>): Promise<T> => {
  try {
    return await read();
  } catch (e) {
    if (e instanceof Error && 'response' in e) throw e;
    throw fetchFailedError(e);
  }
};

const getUserDoc = async (uid: string): Promise<ManagementUserDoc | undefined> => {
  const snapshot = await getDoc(doc(fireStore, MANAGEMENT_USERS_COLLECTION, uid));
  return snapshot.exists() ? (snapshot.data() as ManagementUserDoc) : undefined;
};

const getSubCollection = async <T>(uid: string, name: string): Promise<T[]> => {
  const snapshot = await getDocs(collection(fireStore, MANAGEMENT_USERS_COLLECTION, uid, name));
  return snapshot.docs.map((d) => d.data() as T);
};

const paginate = <T>(items: T[], limit?: number, offset?: number): T[] => {
  const start = Math.max(offset ?? 0, 0);
  return limit === undefined ? items.slice(start) : items.slice(start, start + Math.max(limit, 0));
};

// SQLの「DESC で NULL は末尾」と同じ並びにする
const compareDesc = (a: string | number | null | undefined, b: string | number | null | undefined): number => {
  if (a === b) return 0;
  if (a === null || a === undefined || a === '') return 1;
  if (b === null || b === undefined || b === '') return -1;
  return a < b ? 1 : -1;
};

const byEndDateDesc = (a: Project, b: Project) => compareDesc(a.endDate, b.endDate) || compareDesc(a.id, b.id);

const byCreatedAtDesc = (a: ProjectDoc, b: ProjectDoc) =>
  compareDesc(a.createdAt?.toMillis(), b.createdAt?.toMillis()) || compareDesc(a.id, b.id);

// 画面へは Project 型のみを返す（createdAt は並び替え専用）
const toProject = ({ createdAt: _createdAt, ...project }: ProjectDoc): Project => project;

const isSalesTarget = (project: Project) =>
  project.status === PROJECT_STATUS_CONTRACTED || project.status === PROJECT_STATUS_DEPOSIT_COMPLETED;

const endDateYear = (project: Project) => (project.endDate ? Number(project.endDate.slice(0, 4)) : NaN);

const getAllProjects = (uid: string) => getSubCollection<ProjectDoc>(uid, 'projects');

export const fetchProjects = (
  user: User | null | undefined,
  params: { sort?: number; limit?: number; offset?: number }
) =>
  wrapRead(async () => {
    const projects = await getAllProjects(requireUid(user));
    const sorted = [...projects].sort(params.sort === PROJECT_ORDER_REGISTRATION ? byCreatedAtDesc : byEndDateDesc);
    return paginate(sorted, params.limit, params.offset).map(toProject);
  });

export const fetchProject = (user: User | null | undefined, id: number) =>
  wrapRead(async () => {
    if (!Number.isInteger(id) || id <= 0) throw notFoundError('案件が見つかりません。');
    const snapshot = await getDoc(
      doc(fireStore, MANAGEMENT_USERS_COLLECTION, requireUid(user), 'projects', String(id))
    );
    if (!snapshot.exists()) throw notFoundError('案件が見つかりません。');
    return toProject(snapshot.data() as ProjectDoc);
  });

// API（GetSales）と同じく、契約済・入金済の案件を終了日の年月で集計する
export const fetchSales = (user: User | null | undefined, year: number) =>
  wrapRead(async (): Promise<Sale> => {
    const projects = await getAllProjects(requireUid(user));
    const monthSales = new Array<number>(12).fill(0);
    projects
      .filter((p) => isSalesTarget(p) && endDateYear(p) === year)
      .forEach((p) => {
        const month = Number(p.endDate.slice(5, 7));
        if (month >= 1 && month <= 12) monthSales[month - 1] += Number(p.contractPrice) || 0;
      });
    return { monthSales, totalSales: monthSales.reduce((sum, v) => sum + v, 0) };
  });

export const fetchSaleHistories = (user: User | null | undefined, year: number, limit: number, offset: number) =>
  wrapRead(async () => {
    const projects = await getAllProjects(requireUid(user));
    const filtered = projects.filter((p) => isSalesTarget(p) && endDateYear(p) === year).sort(byEndDateDesc);
    return paginate(filtered, limit, offset).map(toProject);
  });

export const fetchUnpaidProjects = (user: User | null | undefined, limit: number, offset: number) =>
  wrapRead(async () => {
    const projects = await getAllProjects(requireUid(user));
    const filtered = projects.filter((p) => p.status === PROJECT_STATUS_CONTRACTED).sort(byEndDateDesc);
    return paginate(filtered, limit, offset).map(toProject);
  });

export const fetchClients = (user: User | null | undefined) =>
  wrapRead(async () => {
    const clients = await getSubCollection<Client>(requireUid(user), 'clients');
    return [...clients].sort((a, b) => a.id - b.id);
  });

export const fetchClient = (user: User | null | undefined, id: number) =>
  wrapRead(async () => {
    if (!Number.isInteger(id) || id <= 0) throw notFoundError('クライアントが見つかりません。');
    const snapshot = await getDoc(doc(fireStore, MANAGEMENT_USERS_COLLECTION, requireUid(user), 'clients', String(id)));
    if (!snapshot.exists()) throw notFoundError('クライアントが見つかりません。');
    return snapshot.data() as Client;
  });

export const fetchProfile = (user: User | null | undefined) =>
  wrapRead(async (): Promise<ProfileDetail> => {
    const userDoc = await getUserDoc(requireUid(user));
    if (!userDoc?.profile) throw notFoundError('プロフィールが登録されていません。');
    const profile = { ...userDoc.profile };
    delete profile.name;
    return profile;
  });

// 宣伝用プロフィールの表示名など。未登録の場合は undefined
export const fetchProfileUser = (user: User | null | undefined) =>
  wrapRead(async () => {
    const userDoc = await getUserDoc(requireUid(user));
    const profile = userDoc?.profile;
    if (!profile) return undefined;
    return { id: profile.id, name: profile.name ?? profile.accountName, accountName: profile.accountName };
  });

export const fetchPageStyle = (user: User | null | undefined) =>
  wrapRead(async (): Promise<PageStyle> => {
    const userDoc = await getUserDoc(requireUid(user));
    if (!userDoc?.pageStyle) throw notFoundError('ページスタイルが登録されていません。');
    return userDoc.pageStyle;
  });

export const fetchAchievementPhotos = (user: User | null | undefined) =>
  wrapRead(async () => {
    const photos = await getSubCollection<ProfilePhoto>(requireUid(user), 'achievementPhotos');
    return [...photos].sort((a, b) => a.displayNumber - b.displayNumber);
  });
